<template>
    <div class="otc-item-component">
        <card title="Over the Counter Items">
            <form>
                <SearchComponent v-model="item" :disabled="newMode" :label="`Over the Counter Item`"
                    :search-u-r-l="`/otcitem`"></SearchComponent>
                <b-form-row>
                    <b-col>
                        <fg-input v-model="item.name" label="Name" :disabled="disableFields" required>
                        </fg-input>
                    </b-col>
                    <b-col>
                        <fg-input v-model="item.description" label="Description" :disabled="disableFields">
                        </fg-input>
                    </b-col>
                </b-form-row>
                <b-form-row>
                    <b-col>
                        <fg-input v-model.number="item.defaultPrice" label="Default Price" type="number"
                            :disabled="disableFields" required>
                        </fg-input>
                    </b-col>
                    <b-col>
                        <fg-input v-model="item.compareTo" label="Compare To" :disabled="disableFields">
                        </fg-input>
                    </b-col>
                </b-form-row>
                <b-form-row>
                    <b-col>
                        <fg-input v-model="item.upc" label="UPC" :disabled="disableFields">
                        </fg-input>
                    </b-col>
                    <b-col>
                        <fg-input v-model="item.taxCode" label="Tax Code" :disabled="disableFields">
                        </fg-input>
                    </b-col>
                </b-form-row>
                <b-form-row>
                    <b-col>
                        <checkbox v-model="item.active" inline :disabled=disableFields>Active?</checkbox>
                    </b-col>
                    <b-col>
                        <checkbox v-model="item.excludeFromPatientApp" inline :disabled=disableFields>Exclude From
                            Patient App?</checkbox>
                    </b-col>
                </b-form-row>
                <hr>

                <b-row>

                    <!-- Program Selection -->
                    <b-col>
                        <b-row>
                            <b-col>
                                <SearchComponent v-model="selectedProgram" label="Programs" :params="{ active: true }"
                                    search-u-r-l="/Program" :disabled="disableFields">
                                </SearchComponent>
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                <b-list-group>
                                    <b-list-group-item v-for="ip in itemPrograms" :key="`${ip.programId}`">
                                        <b-row>
                                            <b-col><span>{{ ip.programName }}</span></b-col>
                                            <b-col>
                                                <fg-input v-model.number="ip.price" placeholder="Price" type="number"
                                                    :disabled="disableFields">
                                                </fg-input>
                                            </b-col>
                                            <b-col cols="1" class="text-right">
                                                <b-icon icon="x-circle-fill" variant="danger" style="cursor: pointer"
                                                    @click="removeProgram(ip.programId)"></b-icon>
                                            </b-col>
                                        </b-row>
                                    </b-list-group-item>
                                </b-list-group>
                            </b-col>
                        </b-row>
                    </b-col>

                    <!-- Vendor Selection -->
                    <b-col>
                        <b-row>
                            <b-col>
                                <SearchComponent v-model="selectedVendor" :label="`Vendors`" :search-u-r-l="`/Vendor`"
                                    :disabled="disableFields">
                                </SearchComponent>
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                <b-list-group>
                                    <b-list-group-item v-for="iv in itemVendors" :key="`${iv.vendorId}`">
                                        <b-row>
                                            <b-col><span>{{ iv.vendorName }}</span></b-col>
                                            <b-col>
                                                <fg-input v-model.number="iv.cost" placeholder="Cost" type="number"
                                                    :disabled="disableFields">
                                                </fg-input>
                                            </b-col>
                                            <b-col>
                                                <fg-input v-model="iv.vendorItemNbr" placeholder="Item #"
                                                    :disabled="disableFields">
                                                </fg-input>
                                            </b-col>
                                            <b-col cols="1" class="text-right">
                                                <b-icon icon="x-circle-fill" variant="danger" style="cursor: pointer"
                                                    @click="removeVendor(iv.vendorId)"></b-icon>
                                            </b-col>
                                        </b-row>
                                    </b-list-group-item>
                                </b-list-group>
                            </b-col>
                        </b-row>
                    </b-col>

                </b-row>
            </form>

            <NewClearSaveButtons :titles="buttonTitles" :disable="disableFields" :disable-save="!isFormValid"
                :disable-clear="disableFields" @new-clicked="create" @clear-clicked="undoChanges"
                @save-clicked="save" />

        </card>
    </div>
</template>

<script lang="ts">
import axios from 'axios';
import SearchComponent from '@/components/SearchComponent.vue';
import Checkbox from "@/components/Inputs/Checkbox.vue";
import NewClearSaveButtons from '@/components/NewClearSaveButtons.vue';

import { Component, Mixins, Watch } from 'vue-property-decorator';
import { LockHandler } from '@/mixins/LockHandler';
import DisableAutocompleteMixin from '@/mixins/DisableAutocompleteMixin';

import { Program } from '@/models/Program';
import { Vendor } from '@/models/Vendor';
import { OTCItem, OTCItemProgram, OTCItemVendor } from '@/models/OTCItem';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';

@Component({
    name: 'OTCItemDetailsPage',
    components: { SearchComponent, Checkbox, NewClearSaveButtons }
})
export default class OTCItemDetailsPage extends Mixins(LockHandler, DisableAutocompleteMixin) {
    private item: OTCItem = new OTCItem();
    private itemPrograms: OTCItemProgram[] = [];
    private itemVendors: OTCItemVendor[] = [];
    private selectedProgram: Program = new Program();
    private selectedVendor: Vendor = new Vendor();

    private buttonTitles = {
        save: "Save Changes",
        new: "New Item",
        clear: "Clear",
    };

    private searchValue = "";
    private displayName = "";
    private newMode = false;

    get disableFields(): boolean {
        return !(this.item && this.item.id) && !this.newMode;
    }

    create() {
        this.item = new OTCItem();
        this.newMode = true;
    }

    async undoChanges() {
        try {
            const confirmed = await this.$bvModal.msgBoxConfirm('Are you sure you want to clear?', {
                title: 'Confirm',
                okVariant: 'danger',
                centered: true,
            });

            if (confirmed) {
                this.item = new OTCItem();
                this.newMode = false;
                this.itemPrograms = [];
                this.itemVendors = [];
                this.selectedProgram = new Program();
                this.selectedVendor = new Vendor();
            }
        } catch (err) {
            console.log("Error caught in undoChanges()");
            console.error(err);
        }
    }

    get isFormValid() {
        return !!(this.item?.name && typeof this.item.defaultPrice === 'number' && this.item.defaultPrice >= 0);
    }

    save() {
        if (this.item) {
            const data = this.item;
            data.itemPrograms = this.itemPrograms;
            data.itemVendors = this.itemVendors;

            axios.post('/otcitem', data)
                .then(response => {
                    this.item = response.data;
                    this.$notification(NotificationOptions.success("OTC Item Successfully Saved"));
                })
                .catch(error => {
                    this.$notification(NotificationOptions.error(error));
                    console.warn("Error on saving OTC Item", error);
                });
        } else {
            console.warn("We have no OTC Item to save, get a new one?");
        }
    }

    @Watch('item')
    onItemChanged(value: OTCItem, oldValue: OTCItem) {
        if (this.item) {
            if (value.id) {
                //Get programs associated to the OTC Item
                axios.get(`/otcitem/${value.id}/programs`)
                    .then(response => {
                        this.itemPrograms = response.data;
                    })
                    .catch(error => {
                        console.warn(error);
                    });

                //Get vendros associated to the OTC Item
                axios.get(`/otcitem/${value.id}/vendors`)
                    .then(response => {
                        this.itemVendors = response.data;
                    })
                    .catch(error => {
                        console.warn(error);
                    });
            }
        }
    }

    @Watch('selectedProgram') onSelectedProgramChanged(value: Program | null, oldValue: Program | null) {
        if (value?.id) {
            this.addProgram(value);
        }
    }

    addProgram(newProgram: Program | null = null) {
        const selectedProgram: Program | null = newProgram || this.selectedProgram;
        if (selectedProgram) {
            const wasAdded = this.itemPrograms.filter(ip => ip.programId == selectedProgram.id)[0];
            if (!wasAdded) {
                const newItemProgram = {
                    programName: selectedProgram?.name,
                    itemId: this.item?.id || 0,
                    programId: selectedProgram?.id,
                    price: 0,
                } as OTCItemProgram;

                this.itemPrograms.push(newItemProgram);
            }
        }
    }

    removeProgram(programId: number) {
        this.itemPrograms = this.itemPrograms.filter(p => p.programId != programId);
    }

    @Watch('selectedVendor') onSelectedVendorChanged(value: Vendor | null, oldValue: Vendor | null) {
        if (value?.id) {
            this.addVendor(value);
        }
    }

    addVendor(newVendor: Vendor | null = null) {
        const selectedVendor: Vendor | null = newVendor || this.selectedVendor;
        if (selectedVendor) {
            const wasAdded = this.itemVendors.filter(ip => ip.vendorId == selectedVendor.id)[0];
            if (!wasAdded) {
                const newItemVendor = {
                    vendorName: selectedVendor?.name,
                    itemId: this.item?.id || 0,
                    vendorId: selectedVendor?.id,
                    vendorItemNbr: "",
                    cost: 0,
                } as OTCItemVendor;

                this.itemVendors.push(newItemVendor);
            }
        }
    }

    removeVendor(vendorId: number) {
        this.itemVendors = this.itemVendors.filter(p => p.vendorId != vendorId);
    }

} // close export of class

</script>

<style scoped></style>
